"use client";

import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { useRouter } from "next/router";
import type { Dispatch, ReactNode } from "react";
import { createContext, useEffect, useContext, useMemo, useState } from "react";
import { useIntercom } from "react-use-intercom";
import type { Application } from "shared/types";
import { config } from "../../config";
import type { FlowScreens, GenericVerification } from "./types";
import { Flow, OtherScreens } from "./types";
import {
  ApplicantType,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { instance } from "../../api";
import { flows } from "./constants";
import { useFeatureFlagVariantKey, usePostHog } from "posthog-js/react";
import { datadogRum } from "@datadog/browser-rum";
import { GoogleAnalyticsProvider } from "../google-analytics";

export enum StepAction {
  Next = "NEXT",
  Prev = "PREV"
}

const JourneyFlowContext = createContext<{
  changeStep: (
    action: StepAction | FlowScreens,
    numberOfSteps?: number
  ) => void;
  type: Flow;
  stepToPath: (step?: FlowScreens) => string;
  direction?: StepAction;
  setDirection: Dispatch<StepAction | undefined>;
  currentStep: FlowScreens | undefined;
  flows?: FlowScreens[];
  currentStepProgress: number | undefined;
  maxStepsProgress: number | undefined;
}>({
  changeStep: () => undefined,
  type: Flow.Default,
  stepToPath: () => "",
  setDirection: () => undefined,
  direction: StepAction.Next,
  currentStep: undefined,
  currentStepProgress: undefined,
  maxStepsProgress: undefined
});

const JourneyFlowProvider = ({ children }: { children: ReactNode }) => {
  const flow = useJourneyFlowController();

  return (
    <JourneyFlowContext.Provider value={flow}>
      {children}
    </JourneyFlowContext.Provider>
  );
};

export const useJourneyFlow = () => useContext(JourneyFlowContext);

const ApplicationContext = createContext<{
  application?: UseQueryResult<Application>;
  currentApplicantVerifications?: GenericVerification[];
  id?: string;
}>({});

export const ApplicationProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const { update } = useIntercom();
  const id = router.query.id,
    verificationId = router.query.verificationId;
  const isReady = router.isReady;
  const posthog = usePostHog();

  const currentApplicationMeta = useQuery({
    queryFn: () =>
      instance(`/screen/applications/${String(id)}`).then(
        (m: AxiosResponse<{ application: Application }>) => {
          const verifications = m.data.application.verifications?.filter(
            (v) =>
              ![
                VerificationNames.CreditReport,
                VerificationNames.EvictionReport,
                VerificationNames.CriminalReport,
                VerificationNames.RentalVerification
              ].includes(v.name)
          );
          // replace verifications with filtered ones
          m.data.application.verifications = verifications;

          if (m.data.application.current_applicant?.verifications) {
            const currentVerifications =
              m.data.application.current_applicant.verifications.filter(
                (v) =>
                  ![
                    VerificationNames.CreditReport,
                    VerificationNames.EvictionReport,
                    VerificationNames.CriminalReport,
                    VerificationNames.RentalVerification
                  ].includes(v.name)
              );

            m.data.application.current_applicant.verifications =
              currentVerifications;
          }

          return m.data.application;
        }
      ),
    // Need to refetch verification on every verification id change
    queryKey: ["CURRENT_APPLICATION", id, verificationId],
    enabled: Boolean(id && isReady)
  });

  useEffect(() => {
    // Update intercom_Id
    if (
      currentApplicationMeta.data?.current_applicant?.intercom_id &&
      currentApplicationMeta.data.current_applicant.email
    ) {
      update({
        customAttributes: {
          source: "boomscreen_applicant"
        },
        email: currentApplicationMeta.data.current_applicant.email || "",
        name: currentApplicationMeta.data.current_applicant.first_name || "",
        userId: String(
          currentApplicationMeta.data.current_applicant.intercom_id
        ),
        hideDefaultLauncher: true
      });

      datadogRum.setUserProperty(
        "application_id",
        currentApplicationMeta.data.id
      );

      datadogRum.setUserProperty("applicant", {
        id: currentApplicationMeta.data.current_applicant.id,
        email: currentApplicationMeta.data.current_applicant.email,
        first_name: currentApplicationMeta.data.current_applicant.first_name,
        last_name: currentApplicationMeta.data.current_applicant.last_name,
        middle_name: currentApplicationMeta.data.current_applicant.middle_name,
        phone: currentApplicationMeta.data.current_applicant.phone
      });
      datadogRum.setUserProperty("partner", {
        id: currentApplicationMeta.data.partner.id,
        name: currentApplicationMeta.data.partner.name,
        partner_features: currentApplicationMeta.data.partner.partner_features
      });
      datadogRum.setUserProperty(
        "feature_flags",
        posthog.featureFlags.getFlags()
      );

      posthog.setPersonProperties({
        applicant_email: currentApplicationMeta.data.current_applicant.email,
        env: config.environment,
        partner_id: currentApplicationMeta.data.partner.id
      });
    }
  }, [currentApplicationMeta.data]);

  useEffect(() => {
    if (id) currentApplicationMeta.refetch();
  }, [id, isReady]);

  return (
    <ApplicationContext.Provider
      value={{
        application: currentApplicationMeta,
        id: id && isReady ? String(id) : undefined,
        currentApplicantVerifications: currentApplicationMeta.data
          ?.current_applicant?.verifications as
          | undefined
          | GenericVerification[]
      }}
    >
      <GoogleAnalyticsProvider>
        <JourneyFlowProvider>{children}</JourneyFlowProvider>
      </GoogleAnalyticsProvider>
    </ApplicationContext.Provider>
  );
};

export const useApplication = () => useContext(ApplicationContext);

const useJourneyFlowController = () => {
  const { id, application, currentApplicantVerifications } = useApplication();
  const [direction, setDirection] = useState<StepAction | undefined>(
    StepAction.Next
  );
  const homeScreenFlowVariant = useFeatureFlagVariantKey(
    "restricted-application-flow"
  );

  const [currentStep, setCurrentStep] = useState<FlowScreens>();
  const flow =
    homeScreenFlowVariant === "no-home-screen"
      ? Flow.BypassHomeScreen
      : Flow.Default;

  const isStepVerification = (step: FlowScreens) =>
    Object.values(VerificationNames).includes(step as VerificationNames);

  const flowPath: FlowScreens[] = useMemo(() => {
    if (!currentApplicantVerifications) return [];

    return flows[flow].filter((fl) => {
      // Leave not verification screens and verification screens that are present in verifications
      if (!isStepVerification(fl)) return true;
      return currentApplicantVerifications.some((v) => v.name === fl);
    });
  }, [flow, flows, currentApplicantVerifications]);

  const maxStepsProgress = useMemo(() => {
    return flowPath.reduce((acc, path) => {
      const verification = currentApplicantVerifications?.find(
        (v) => v.name === path
      );
      if (verification) return acc + (verification.progress?.total_steps || 0);
      return acc + 1;
    }, 0);
  }, [flowPath]);

  const currentStepProgress = useMemo(() => {
    if (!currentStep) return 0;

    let res = 0;

    const idx = flowPath.indexOf(currentStep);
    const doneSteps = flowPath.slice(0, idx);

    doneSteps.forEach((path) => {
      const verification = currentApplicantVerifications?.find(
        (v) => v.name === path
      );
      if (verification) res += verification.progress?.total_steps || 0;
      else res += 1;
    });

    //add current screen
    const verification = currentApplicantVerifications?.find(
      (v) => v.name === currentStep
    );
    if (verification) res += verification.progress?.filled_steps || 0;
    else res += 1;

    return res;
  }, [currentStep, currentApplicantVerifications]);

  const changeStep = (action: StepAction | FlowScreens, numberOfSteps = 1) => {
    if (!currentStep) return;
    if (Object.values(StepAction).includes(action as StepAction))
      setDirection(action as StepAction);
    if (Object.values(StepAction).includes(action as StepAction)) {
      const currentIndex = flowPath.indexOf(currentStep);
      if (currentIndex === -1) {
        setCurrentStep(OtherScreens.ToApplications);
        return;
      }

      if (action === StepAction.Next) {
        const nextStep: FlowScreens = flowPath[currentIndex + numberOfSteps];
        setCurrentStep(nextStep);
      } else if (action === StepAction.Prev) {
        const prevStep = flowPath[currentIndex - numberOfSteps];
        setCurrentStep(prevStep);
      }
    } else setCurrentStep(action as FlowScreens);
  };

  // TODO: may backfire because we refetch verifications only if ID changes
  const isVerificationCompleted = (step: FlowScreens): boolean => {
    // check if step screen is instance of verificationNames
    const isVerification = isStepVerification(step);

    if (!isVerification) return false;

    // find verification by name
    const verification = currentApplicantVerifications?.find(
      (v) => v.name === step
    );

    if (!verification) return false;

    // check if verification is finished
    return [
      VerificationStatus.Finished,
      VerificationStatus.FinishedWithWarnings,
      VerificationStatus.ManuallyApproved,
      VerificationStatus.Verified
      // status Failed is correct finished in this context
    ].includes(verification.status);
  };

  // should be used on mount to understand what is currentStep
  const calculateCurrentStep = (step?: FlowScreens): FlowScreens => {
    // If current step is present -- don't recalculate it
    if (currentStep) return currentStep;
    if (step) return step;

    const current = flowPath.find((s) => {
      if (isStepVerification(s)) {
        if (isVerificationCompleted(s as FlowScreens)) return false;
        return true;
      }

      switch (s) {
        case OtherScreens.Invite:
        case OtherScreens.NotFound:
        case OtherScreens.CheckOut:
          // Requirements check redirect is inside of Checkout component itself
          return application?.data?.current_applicant?.can_checkout;
        case OtherScreens.MoveInDate:
          return !application?.data?.desired_move_in_date;
        default:
          return false;
      }
    });

    return current || flowPath[0];
  };

  const stepToPath = (argStep?: FlowScreens): string => {
    const step = argStep || currentStep;

    if (application?.data?.current_applicant?.can_checkout)
      return `/applications/${id}/checkout`;

    if (!step) return "/";

    if (isStepVerification(step)) {
      const isIdvAndFailed = (v: GenericVerification) => {
        return (
          v.name === VerificationNames.IdentityVerification &&
          v.status === VerificationStatus.Failed &&
          Boolean(
            application?.data?.verification_template.settings[
              application.data.current_applicant?.role ||
                ApplicantType.Applicant
            ].identity_verification?.prevent_apply_if_failed
          )
        );
      };

      // IDV failed, prevent checkout and submission
      if (
        (step === VerificationNames.SubmissionDisclosure ||
          step === OtherScreens.CheckOut) &&
        currentApplicantVerifications?.find(isIdvAndFailed)
      )
        return `/applications/${id}/error/unable-to-checkout`;

      // Prevent user from going to income verification if IDV is not finished
      if (
        (step === VerificationNames.IncomeVerification ||
          step === VerificationNames.VerifiedIncomeVerification) &&
        currentApplicantVerifications?.find(isIdvAndFailed)
      )
        return `/applications/${id}/error/idv-not-finished`;

      if (!direction) return `/applications/${id}/verifications/${step}`;

      return `/applications/${id}/verifications/${step}${
        ![
          VerificationNames.IdentityVerification,
          VerificationNames.VerifiedIncomeVerification
        ].includes(step as VerificationNames)
          ? `/steps/${direction === StepAction.Next ? "first" : "last"}`
          : ""
      }`;
    }

    switch (step) {
      case OtherScreens.Invite:
        return `/applications/${id}/applicants/invite?skip=true`;
      case OtherScreens.MoveInDate:
        return `/applications/${id}/move-in-date`;
      case OtherScreens.CheckOut:
        return `/applications/${id}/checkout`;
      case OtherScreens.ToApplications:
        return "/";
      case OtherScreens.Requirements:
        return `/applications/${id}/error/requirements`;
      case OtherScreens.PlaidError:
        return `/applications/${id}/error/plaid-error`;
      case OtherScreens.IDVRequiredWaitForIt:
        return `/applications/${id}/error/unable-to-checkout`;
      default:
        //WARN: may result in infinite loop
        return `/applications/${id}`;
    }
  };

  useEffect(() => {
    if (flowPath.length && application?.data)
      setCurrentStep(calculateCurrentStep());

    return () => {
      setDirection(undefined);
      setCurrentStep(undefined);
    };
  }, [id, flowPath, application?.data]);

  return {
    type: flow,
    changeStep,
    stepToPath,
    setDirection,
    direction,
    currentStep,
    flows: flowPath,
    currentStepProgress,
    maxStepsProgress
  };
};
